import { reactKeyWrapper } from '@ms/yammer-i18n';
import { useEffect, useMemo } from 'react';

import { loadTranslations } from '../domains/translations/actions';
import { useHaveTranslationsCompleted, useTranslationDataForFeature } from '../domains/translations/selectors';
import { getOrCreateTranslationLoadPromise, suspendWith } from '../domains/translations/suspenseCache';
import { useCurrentLocale, usePseudolocMode } from '../hooks/selectors';
import type { StringWrapperCtor } from '../state/types';

const useTranslationsInternal = <T>(from: StringWrapperCtor<T>, shouldSuspend: boolean): T => {
  const rawTranslations = useTranslationDataForFeature(from.feature);
  const locale = useCurrentLocale();
  const pseudolocMode = usePseudolocMode();
  const haveTranslationsCompleted = useHaveTranslationsCompleted(from);

  useEffect(() => {
    void loadTranslations({
      locale,
      feature: from.feature,
    });
  }, [from, locale]);

  const strings = useMemo(
    () => new from(locale, pseudolocMode, rawTranslations, reactKeyWrapper),
    [from, locale, rawTranslations, pseudolocMode]
  );

  if (shouldSuspend && !haveTranslationsCompleted) {
    suspendWith(getOrCreateTranslationLoadPromise(locale, from.feature));
  }

  return strings;
};

export type UseTranslations = <T>(Strings: StringWrapperCtor<T>) => T;

export const useTranslations: UseTranslations = (from) => useTranslationsInternal(from, false);

export const useTranslationsWithSuspense: UseTranslations = (from) => useTranslationsInternal(from, true);
